<template>
  <VDialogV3
    id="demand-card"
    position="right"
    :width="dialogWidth"
    :entity="localDemand"
    :context-menu="isBuyer ? DemandsService.contextMenu : null"
    :on-context-menu-action="onContextMenuAction"
    :tabs="tabs"
    :submit-method="submit"
    :submit-disabled="$core.currentPartner.isSeller"
    :active-tab="activeTab"
    @update:active-tab="activeTab = $event"
  >
    <template #header>
      <div
        class="title"
        :title="localDemand?.productName"
      >
        Потребность "{{ localDemand?.productName }}"
      </div>
    </template>

    <section
      v-if="activeTab.id === 'main'"
    >
      <VInput
        layout="vertical"
        label="Наименование"
        disabled
        :value="localDemand.productName"
      />

      <VInput
        layout="vertical"
        label="Артикул товара"
        disabled
        :value="localDemand.product.code"
      >
        <template #helper-text v-if="localDemand.hasTender">
          Тендер:
          <VLink @click="TendersService.showTenderCard(localDemand.tender)">
            {{ localDemand.tender.id }}
          </VLink>
        </template>
      </VInput>

      <VCheckboxGroup
        v-model="activeOrderType"
        label="Тип заказа"
        layout="vertical"
        type-radio
        checkbox-layout="horizontal"
        option-label="name"
        disabled
        :options="ordersType"
      />

      <VInputNumberRusV2
        v-model="localDemand.qtyDesired"
        :type="Qty"
        label="Количество"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :unit="localDemand.unit?.name"
      />

      <VDateInput
        v-if="!isPreorder"
        v-model="localDemand.desiredReceiptDate"
        label="Дата поставки"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :validate-method="validateDate"
      />

      <VInputNumberRusV2
        v-if="isPreorder"
        v-model="localDemand.preorderMonthlyReserve"
        :type="Qty"
        label="Страховой запас"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :unit="localDemand.unit?.name"
        :validate-method="(value: Qty) => localDemand.validatePreorderMonthlyReserve(value)"
      />

      <VDateRangeInput
        v-if="isPreorder"
        v-model="localDemand.preorderShipmentPeriod"
        label="Дата поставки"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :validate-method="validateDate"
      />

      <VSelect
        v-if="isBuyer"
        v-model="localDemand.responsibleBuyer"
        label="Ответственный"
        placeholder="Введите ФИО ответственного"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :search-function="pattern => PersonsService.searchPersons(pattern, $core.currentPartner.id)"
      />
    </section>

    <template
      v-if="activeTab.id === 'supplyOffers'"
    >
      <SupplyOffersRegistry
        :product="localDemand.product"
        class="fill"
      />
    </template>


    <ProductCharacteristicsReadModeBlock
      v-if="localDemand?.product && activeTab.id === 'characteristic'"
      :product="localDemand.product"
      hide-edit
    />

    <section v-if="activeTab.id === 'buyer'">
      <VInput
        label="Покупатель"
        layout="vertical"
        disabled
        :value="localDemand.buyer.name"
      />

      <VSelectV2
        v-model="localDemand.buyerCompany"
        label="Юр. лицо"
        :disabled="!isBuyer"
        :data="buyerCompanySource"
        :required="isBuyer"
      />

      <VSelectV2
        v-model="localDemand.buyerStore"
        label="Склад"
        :disabled="!isBuyer"
        :data="buyerStoreSource"
        :required="isBuyer"
      />

      <VSelectV2
        v-model="localDemand.buyerProductionSite"
        label="Производственная площадка"
        :disabled="!isBuyer"
        :data="buyerProductionSiteSource"
      />
    </section>

    <FilesList
      v-if="activeTab.id === 'files'"
      :entities="fileEntities"
    />
  </VDialogV3>
</template>

<script lang="ts">

import DateSimple from '@/core/types/DateSimple';
import DemandsService from '@/domains/Demand/DemandsService';
import PersonsService from '@/domains/Persons/PersonsService';
import EntityPointer from '@/domains/EntityPointer/EntityPointer';
import TendersService from "@/domains/Tenders/TendersService";
import Demand from '@/domains/Demand/Demand';
import Qty from '@/core/types/Qty';
import CompaniesService from '@/domains/Companies/CompaniesService';
import StoresService from '@/domains/Stores/StoresService';
import ProductionSitesService from '@/domains/ProductionSites/ProductionSitesService';

import ProductCharacteristicsReadModeBlock
  from '@/domains/Products/components/ProductCharacteristicsReadModeBlock/ProductCharacteristicsReadModeBlock.vue';
import SupplyOffersRegistry
  from '@/domains/SupplyOffers/components/SupplyOffersRegistry/SupplyOffersRegistry.vue';

export default defineComponent({
  name: 'DemandCard',

  components: {
    SupplyOffersRegistry,
    ProductCharacteristicsReadModeBlock,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
  },

  setup() {
    return {
      DemandsService,
      PersonsService,
      TendersService,
      /** Класс нужен шаблону для :type у VInputNumberRusV2 */
      Qty,
    };
  },

  data() {
    /** Потребность всегда существующая — источники грузим по фокусу.
     *  localDemand загружается в init(), поэтому зависимости через ?. */
    return {
      buyerCompanySource: this.$core.loader.select<Demand>(({buyer}) =>
        CompaniesService.getList({ partnerId: buyer.id })
      )
        .dependsOn({ buyer: () => this.localDemand?.buyer })
        .preloadOnFocus(),

      buyerStoreSource: this.$core.loader.select<Demand>(({buyer}) =>
        StoresService.getList({ partnerId: buyer.id })
      )
        .dependsOn({
          buyer:        () => this.localDemand?.buyer,
          buyerCompany: () => this.localDemand?.buyerCompany,
        })
        .default((options, {buyerCompany}) =>
          options.find(_option => _option.id === buyerCompany?.defaultStore?.id)
        )
        .preloadOnFocus(),

      buyerProductionSiteSource: this.$core.loader.select<Demand>(({buyerCompany}) =>
        ProductionSitesService.getList(null, buyerCompany)
      )
        .dependsOn({ buyerCompany: () => this.localDemand?.buyerCompany })
        .preloadOnFocus(),

      localDemand: undefined as unknown as Demand,

      ordersType: [
        {id: 'byOrder', name: 'Обычный заказ'},
        {id: 'byPreorder', name: 'По предзаказу'},
      ],
      activeOrderType: null as { id: string; name: string } | null,

      activeTab: undefined,
    };
  },

  computed: {
    /** Текущий пользователь — покупатель */
    isBuyer(): boolean {
      return this.$core.currentPartner?.isBuyer === true;
    },

    /** Текущий пользователь — поставщик */
    isSeller(): boolean {
      return this.$core.currentPartner?.isSeller === true;
    },

    /** Табы карточки. Таб поставщиков доступен только покупателю, таб файлов — тоже. */
    tabs() {
      const baseTabs = [
        {id: 'main', name: 'Основное'},
        {id: 'supplyOffers', name: 'Поставщики'},
        {id: 'characteristic', name: 'Характеристики'},
        {id: 'buyer', name: 'Покупатель'},
      ];

      if (this.isBuyer) {
        baseTabs.push({id: 'files', name: 'Файлы'});
      }

      if (this.isSeller) {
        return baseTabs.filter(tab => tab.id !== 'supplyOffers');
      } else {
        return baseTabs;
      }
    },

    isPreorder(): boolean {
      return this.localDemand?.isPreorderNeeded === true;
    },

    fileEntities() {
      return [new EntityPointer('demand', this.localDemand!.id, this.localDemand!.toString())];
    },

    /** На вкладке разрешений таблица широкая, остальным вкладкам лишнее место ни к чему */
    dialogWidth(): string {
      if (this.activeTab?.id === 'supplyOffers') {
        return '1210px';
      }
      else {
        return '605px';
      }
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    /** Загружает потребность: id или число в demand → get, объект → клон. */
    async init(): Promise<void> {
      if (this.id !== null) {
        this.localDemand = await DemandsService.get(this.id!);
      }
      // TODO else -> new??

      this.activeOrderType = this.ordersType.find(type => (this.localDemand!.isPreorderNeeded
        ? type.id === 'byPreorder'
        : type.id === 'byOrder')) ?? null;
    },

    /**
     * Пункт контекстного меню что-то изменил. Удалённую потребность сразу
     * закрываем — хост получит true и обновится, остальное перечитываем.
     * Перечитываем через init, чтобы не дублировать загрузку потребности.
     */
    async onContextMenuAction(): Promise<void> {
      if (this.localDemand!.isDeleted === true) {
        this.$core.dialogs.DemandCard.resolve();
      }
      else {
        await this.init();
      }
    },

    /** Сохраняет потребность. Валидацию формы и resolve() при успехе делает VDialogV3. */
    async submit(): Promise<boolean> {
      return await DemandsService.update(this.localDemand!);
    },

    /** Запрещает прошедшую дату поставки. */
    validateDate(date: unknown): string | null {
      if ((date instanceof DateSimple) === false) return null;

      if (date.isFuture() === false) {
        return 'Нельзя указать прошедшую дату';
      }

      return null;
    },
  },
});
</script>
